import { Switch } from "antd";
import type { ReactNode } from "react";

import type { AutoTopupConfigResponse } from "store/slices/reduxApi/billing";

// Shared validation bounds. Keep in sync with the server-side bounds in
// `auto-topup/config.ts` (server is the source of truth — these mirror it
// for client-side error messages so admins don't need a round trip).
export const PACK_MIN = 50;
export const PACK_MAX = 2500;
export const CAP_MAX = 10000;

// First-time defaults when the org has no saved auto-topup config yet. The
// form pre-fills with these so the admin sees a ready-to-save form instead of
// three empty inputs they have to figure out themselves. All surfaces share
// the same fallback (Manage Plan modal `TopUpView`, /spending
// `AutoTopUpSettingsModal`, /usage `AutoTopUpPanel`) so the first-run
// experience is consistent regardless of where the admin lands.
export const DEFAULT_PACK_CREDITS = 100;
export const DEFAULT_MONTHLY_CAP = 1000;
export const DEFAULT_TRIGGER_BUFFER = 20;

export type AutoFormState = {
  enabled: boolean;
  packCredits: string;
  monthlyCreditCap: string;
  triggerBufferCredits: string;
};

export type AutoErrors = {
  packCredits?: string;
  monthlyCreditCap?: string;
  triggerBufferCredits?: string;
};

export function formatUsd(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function isWholeNumber(raw: string): boolean {
  if (raw.trim() === "") return false;
  const n = Number(raw);
  return Number.isFinite(n) && Number.isInteger(n);
}

export function autoFormFromServer(
  data: AutoTopupConfigResponse,
): AutoFormState {
  // Null-from-server means "first-run" (no saved config yet); fall back to the
  // shared first-run defaults so every surface — modal, /spending, /usage —
  // shows the same starter values. After the first save the server returns
  // concrete numbers and the fallback no longer applies.
  return {
    enabled: data.enabled,
    packCredits: data.pack_credits?.toString() ?? String(DEFAULT_PACK_CREDITS),
    monthlyCreditCap:
      data.monthly_credit_cap?.toString() ?? String(DEFAULT_MONTHLY_CAP),
    triggerBufferCredits:
      data.trigger_buffer_credits?.toString() ?? String(DEFAULT_TRIGGER_BUFFER),
  };
}

export function autoFormEqual(a: AutoFormState, b: AutoFormState): boolean {
  return (
    a.enabled === b.enabled &&
    a.packCredits === b.packCredits &&
    a.monthlyCreditCap === b.monthlyCreditCap &&
    a.triggerBufferCredits === b.triggerBufferCredits
  );
}

export function validateAutoForm(form: AutoFormState): AutoErrors {
  const errors: AutoErrors = {};
  if (!form.enabled) return errors;

  if (!isWholeNumber(form.packCredits)) {
    errors.packCredits = "Whole number required";
  } else {
    const pack = Number(form.packCredits);
    if (pack < PACK_MIN || pack > PACK_MAX) {
      errors.packCredits = `Must be between ${PACK_MIN} and ${PACK_MAX}`;
    }
  }

  const pack = Number(form.packCredits);
  if (!isWholeNumber(form.monthlyCreditCap)) {
    errors.monthlyCreditCap = "Whole number required";
  } else {
    const cap = Number(form.monthlyCreditCap);
    if (cap > CAP_MAX) {
      errors.monthlyCreditCap = `Must be <= ${CAP_MAX}`;
    } else if (isWholeNumber(form.packCredits) && cap < pack) {
      errors.monthlyCreditCap = "Must be >= pack size";
    }
  }

  if (form.triggerBufferCredits.trim() !== "") {
    if (!isWholeNumber(form.triggerBufferCredits)) {
      errors.triggerBufferCredits = "Whole number required";
    } else {
      const buf = Number(form.triggerBufferCredits);
      if (buf < 0) {
        errors.triggerBufferCredits = "Must be >= 0";
      } else if (isWholeNumber(form.packCredits) && buf > pack) {
        errors.triggerBufferCredits = "Must be <= pack size";
      }
    }
  }

  return errors;
}

type AutoTopUpFormFieldsProps = {
  form: AutoFormState;
  onFormChange: (form: AutoFormState) => void;
  errors: AutoErrors;
  /** Per-credit price in cents, from `autoTopupConfig.unit_amount_cents`. */
  unitAmountCents: number;
  /** When false the switch is disabled and no charge can be configured. */
  hasDefaultPaymentMethod: boolean;
  /** Disable all controls (e.g. while a charge is in flight in TopUpView). */
  disabled?: boolean;
  /**
   * Optional content rendered at the right edge of the switch row. Used by
   * TopUpView to surface the "Unsaved · saves on confirm" amber pill when
   * the form is dirty in the merged charge+save flow. /spending doesn't
   * need this slot — saving there is its own primary action.
   */
  switchRowSlot?: ReactNode;
};

/**
 * Auto top up form fields — shared between the Manage Plan modal's
 * `TopUpView` and the standalone `AutoTopUpSettingsModal` on /spending so
 * the two surfaces stay visually identical (single source of truth for
 * the field grid, validation copy, and inline `= $X.XX` previews).
 *
 * The component is purely presentational: form state + errors are owned
 * by the parent so the parent can decide when (and whether) to persist.
 * TopUpView combines the form save with a manual charge; /spending fires
 * a standalone PATCH.
 */
export function AutoTopUpFormFields({
  form,
  onFormChange,
  errors,
  unitAmountCents,
  hasDefaultPaymentMethod,
  disabled = false,
  switchRowSlot,
}: AutoTopUpFormFieldsProps) {
  const packCharge = isWholeNumber(form.packCredits)
    ? Number(form.packCredits) * unitAmountCents
    : null;
  const capCharge = isWholeNumber(form.monthlyCreditCap)
    ? Number(form.monthlyCreditCap) * unitAmountCents
    : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Intro paragraph removed — both consumers (TopUpView's Auto refill
          subsection + AutoTopUpSettingsModal) render their own section
          header + explainer above. Keeping a duplicate here read like
          stutter copy. */}
      <div className="flex items-center gap-3">
        <Switch
          data-test="auto-topup-switch"
          checked={form.enabled}
          disabled={!hasDefaultPaymentMethod || disabled}
          onChange={(checked) => onFormChange({ ...form, enabled: checked })}
        />
        {/* Single-line label. Was previously a 2-line "Auto-refill /
            Off — turn on to configure" with the second line flipping copy.
            Status is already conveyed by the switch position; the second
            line was redundant. */}
        <span className="text-[13px] font-medium text-[#1f2633]">
          Auto-refill
        </span>
        {switchRowSlot ? <div className="ml-auto">{switchRowSlot}</div> : null}
      </div>

      {/* Field grid is ALWAYS rendered so the modal doesn't jump in height
          when the toggle flips. When auto-refill is off the inputs are
          disabled, the inline numeric values are hidden (color: transparent
          on the input — value stays in form state, just invisible) and the
          price-preview hints fade out. Flipping the toggle on restores
          everything in place. */}
      <div
        data-test="auto-topup-fields"
        className="grid grid-cols-[1fr_1fr_1.6fr] gap-3"
      >
        <AutoField
          label="Credits per refill"
          value={form.packCredits}
          onChange={(v) => onFormChange({ ...form, packCredits: v })}
          error={errors.packCredits}
          min={PACK_MIN}
          max={PACK_MAX}
          pricePreview={
            packCharge != null ? `= ${formatUsd(packCharge)} / charge` : null
          }
          disabled={disabled}
          hideValue={!form.enabled}
        />
        <AutoField
          label="Monthly team limit"
          value={form.monthlyCreditCap}
          onChange={(v) => onFormChange({ ...form, monthlyCreditCap: v })}
          error={errors.monthlyCreditCap}
          min={isWholeNumber(form.packCredits) ? Number(form.packCredits) : 0}
          max={CAP_MAX}
          pricePreview={
            capCharge != null ? `= ${formatUsd(capCharge)} / month` : null
          }
          disabled={disabled}
          hideValue={!form.enabled}
        />
        <AutoField
          label="Refill when balance drops below"
          value={form.triggerBufferCredits}
          onChange={(v) => onFormChange({ ...form, triggerBufferCredits: v })}
          error={errors.triggerBufferCredits}
          min={0}
          max={
            isWholeNumber(form.packCredits)
              ? Number(form.packCredits)
              : undefined
          }
          pricePreview={null}
          disabled={disabled}
          hideValue={!form.enabled}
        />
      </div>
    </div>
  );
}

function AutoField({
  label,
  value,
  onChange,
  error,
  min,
  max,
  pricePreview,
  disabled,
  hideValue = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  min?: number;
  max?: number;
  pricePreview: string | null;
  disabled: boolean;
  /**
   * When true, the input is forced into the disabled state with its visible
   * text hidden (color: transparent) and the price-preview hint hidden.
   * Used by the auto-refill toggle off state — fields stay rendered so the
   * modal doesn't jump in height when the toggle flips, but the user
   * doesn't see meaningless numbers.
   */
  hideValue?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label
        className={`text-[11px] ${hideValue ? "text-[#b0b7c1]" : "text-[#6c7689]"}`}
      >
        {label}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || hideValue}
        className={`h-8 rounded border px-2 text-[13px] disabled:cursor-not-allowed ${
          error ? "border-[#b91c1c]" : "border-[#e8eaed]"
        } ${
          hideValue
            ? "!bg-[#f9fafb] !text-transparent placeholder:text-transparent"
            : "disabled:opacity-60"
        }`}
      />
      {error ? (
        <span className="text-[11px] text-[#b91c1c]">{error}</span>
      ) : pricePreview ? (
        <span
          className={`text-[11px] tabular-nums ${hideValue ? "text-transparent" : "text-[#6c7689]"}`}
        >
          {pricePreview}
        </span>
      ) : null}
    </div>
  );
}
